import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import DatePicker from 'react-datepicker';
import { useDispatch } from 'react-redux';

import 'react-datepicker/dist/react-datepicker.css';

import { crearProyecto, crearClienteProyecto } from '../../../actions/proyectos';

import AgregarClienteModal from './AgregarClienteModal';

const formatYYYYMMDD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const NoOptionsClientes = (props) => {
    const inputValue = (props.selectProps.inputValue || '').trim();

    return (
        <components.NoOptionsMessage {...props}>
            <div className="text-muted mb-2">Sin resultados{inputValue ? ` para "${inputValue}"` : ''}</div>
            <button
                type="button"
                className="btn btn-outline-primary btn-sm w-100"
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof props.selectProps.onAgregarCliente === 'function') {
                        props.selectProps.onAgregarCliente();
                    }
                }}
            >
                Agregar nuevo cliente
            </button>
        </components.NoOptionsMessage>
    );
};

const NuevoProyectoModal = ({ show, onHide, onSuccess, opcionesClientes }) => {
    const dispatch = useDispatch();

    const [clienteSel, setClienteSel] = useState(null);
    const [clienteInput, setClienteInput] = useState('');
    const [showNuevoCliente, setShowNuevoCliente] = useState(false);
    const [savingCliente, setSavingCliente] = useState(false);
    /* Oculta este modal (sin desmontarlo ni perder lo capturado) mientras se agrega un cliente nuevo */
    const [ocultoPorCliente, setOcultoPorCliente] = useState(false);
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [direccion, setDireccion] = useState('');
    const [fechaInicioTentativa, setFechaInicioTentativa] = useState(null);
    const [fechaFinTentativa, setFechaFinTentativa] = useState(null);
    const [fechaInicioReal, setFechaInicioReal] = useState(null);
    const [fechaFinReal, setFechaFinReal] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (show) return;

        setClienteSel(null);
        setClienteInput('');
        setShowNuevoCliente(false);
        setSavingCliente(false);
        setNombre('');
        setDescripcion('');
        setDireccion('');
        setFechaInicioTentativa(null);
        setFechaFinTentativa(null);
        setFechaInicioReal(null);
        setFechaFinReal(null);
        setGuardando(false);
        setOcultoPorCliente(false);
    }, [show]);

    const abrirNuevoCliente = () => {
        setOcultoPorCliente(true);
        setShowNuevoCliente(true);
    };

    const cerrarNuevoCliente = () => {
        setShowNuevoCliente(false);
        setOcultoPorCliente(false);
    };

    const guardarNuevoCliente = async (data) => {
        setSavingCliente(true);
        const cliente = await dispatch(crearClienteProyecto(data));
        setSavingCliente(false);

        if (!cliente?._id) return;

        setClienteSel({ value: cliente._id, label: cliente.nombre });
        setClienteInput('');
        cerrarNuevoCliente();
    };

    const puedeGuardar = useMemo(() => {
        return Boolean(clienteSel?.value && nombre.trim().length > 0 && !guardando);
    }, [clienteSel, nombre, guardando]);

    const onGuardar = async () => {
        if (!puedeGuardar) return;

        setGuardando(true);
        const ok = await dispatch(crearProyecto({
            id_cliente: clienteSel?.value,
            nombre: nombre.trim(),
            descripcion: descripcion.trim() || null,
            direccion: direccion.trim() || null,
            fecha_inicio_tentativa: formatYYYYMMDD(fechaInicioTentativa),
            fecha_fin_tentativa: formatYYYYMMDD(fechaFinTentativa),
            fecha_inicio_real: formatYYYYMMDD(fechaInicioReal),
            fecha_fin_real: formatYYYYMMDD(fechaFinReal)
        }));
        setGuardando(false);

        if (!ok) return;

        if (typeof onSuccess === 'function') {
            onSuccess();
        } else if (typeof onHide === 'function') {
            onHide();
        }
    };

    return (
        <Fragment>
            <Modal show={show && !ocultoPorCliente} onHide={onHide} backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Agregar proyecto</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3">
                        <div className="text-muted small mb-1">Cliente</div>
                        <Select
                            options={opcionesClientes}
                            value={clienteSel}
                            inputValue={clienteInput}
                            onInputChange={(val, meta) => {
                                if (meta.action === 'input-change') {
                                    setClienteInput(val);
                                }
                            }}
                            onChange={(opt) => { setClienteSel(opt || null); setClienteInput(''); }}
                            isClearable
                            placeholder="Cliente..."
                            components={{ NoOptionsMessage: NoOptionsClientes }}
                            onAgregarCliente={abrirNuevoCliente}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="text-muted small mb-1">Nombre del proyecto</div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre del proyecto"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="text-muted small mb-1">Descripción</div>
                        <textarea
                            className="form-control"
                            placeholder="Descripción"
                            rows={3}
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="text-muted small mb-1">Dirección</div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Dirección"
                            value={direccion}
                            onChange={(e) => setDireccion(e.target.value)}
                        />
                    </div>

                    <div className="row">
                        <div className="col-6 mb-3">
                            <div className="text-muted small mb-1">Fecha inicio tentativa</div>
                            <DatePicker
                                selected={fechaInicioTentativa}
                                onChange={(d) => setFechaInicioTentativa(d)}
                                className="form-control"
                                placeholderText="Fecha inicio tentativa"
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                        </div>
                        <div className="col-6 mb-3">
                            <div className="text-muted small mb-1">Fecha fin tentativa</div>
                            <DatePicker
                                selected={fechaFinTentativa}
                                onChange={(d) => setFechaFinTentativa(d)}
                                className="form-control"
                                placeholderText="Fecha fin tentativa"
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                        </div>
                        <div className="col-6 mb-3">
                            <div className="text-muted small mb-1">Fecha inicio real</div>
                            <DatePicker
                                selected={fechaInicioReal}
                                onChange={(d) => setFechaInicioReal(d)}
                                className="form-control"
                                placeholderText="Fecha inicio real"
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                        </div>
                        <div className="col-6 mb-3">
                            <div className="text-muted small mb-1">Fecha fin real</div>
                            <DatePicker
                                selected={fechaFinReal}
                                onChange={(d) => setFechaFinReal(d)}
                                className="form-control"
                                placeholderText="Fecha fin real"
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={onHide} disabled={guardando}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={onGuardar} disabled={!puedeGuardar}>
                        Guardar
                    </button>
                </Modal.Footer>
            </Modal>

            <AgregarClienteModal
                show={showNuevoCliente}
                onHide={cerrarNuevoCliente}
                onSave={guardarNuevoCliente}
                loading={savingCliente}
            />
        </Fragment>
    );
};

export default NuevoProyectoModal;
